import type { ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';

import { IconButton } from '@/ui/button/Button';

interface TeamModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  /** Rendered between the header and the footer (tabs + body). */
  children: ReactNode;
  /** Left side of the footer — usually a subtle hint. */
  footerLeft?: ReactNode;
  /** Right side of the footer — action buttons. */
  footerActions: ReactNode;
  onOpenAutoFocus?: (e: Event) => void;
}

/** The 560px team dialog chrome shared by Create/Join and Invite Members:
 *  bordered header (title + subtitle + close) → body slot → bordered footer. */
export function TeamModalShell({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footerLeft,
  footerActions,
  onOpenAutoFocus,
}: TeamModalShellProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content
          onOpenAutoFocus={onOpenAutoFocus}
          onPointerDownOutside={() => onClose()}
          className="fixed left-1/2 top-1/2 z-50 flex w-140 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-line-strong bg-surface-2 shadow-(--shadow-overlay) animate-fade-in"
        >
          <header className="flex items-center gap-3 border-b border-line-subtle px-4 py-3.25">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Dialog.Title className="m-0 text-sm font-semibold tracking-[-0.01em] text-fg">{title}</Dialog.Title>
              {subtitle && <Dialog.Description className="m-0 text-xs text-fg-tertiary">{subtitle}</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <IconButton size={7} aria-label="Close" className="shrink-0">
                <XIcon size={15} />
              </IconButton>
            </Dialog.Close>
          </header>

          {children}

          <footer className="flex items-center justify-between gap-3 border-t border-line-subtle px-4 py-3">
            <div className="flex min-w-0 items-center">{footerLeft}</div>
            <div className="flex shrink-0 items-center gap-2">{footerActions}</div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
